import { isReviewPreview, previewReviewUrl, readReviewResponse } from './ag-review-preview.js';
import { land, countries as countryCenters } from '@astrography/review-globe-data';
import { reviewCountryCode, reviewCountryFlag } from '@astrography/review-country';

const countryCode = country => reviewCountryCode(country.replace(/^Hong Kong SAR China$/i, 'HK'));
const radians = Math.PI / 180;
// Small regions omitted by the simplified land dataset, present in public reviews.
const countries = { ...countryCenters, SG: [1.35, 103.82], HK: [22.32, 114.17] };
const vector = (latitude, longitude) => {
  const lat = latitude * radians, lon = longitude * radians;
  return [Math.cos(lat) * Math.sin(lon), Math.sin(lat), Math.cos(lat) * Math.cos(lon)];
};
const points = [];
for (let i = 0; i < land.length; i += 2) points.push(vector(land[i], land[i + 1]));
const shortAngle = angle => Math.atan2(Math.sin(angle), Math.cos(angle));

if (!customElements.get('ag-review-globe')) {
  customElements.define('ag-review-globe', class extends HTMLElement {
    #events; #resize; #visibility; #theme; #motion; #ctx; #frame; #last = 0;
    #visible = false; #loaded = false; #paused = false; #hover = false; #focused = false; #modalOpen = false;
    #reviews = []; #index = 0; #elapsed = 0; #lat = 20 * radians; #lon = 0;
    #targetLat = 20 * radians; #targetLon = 0; #point; #width = 560; #anchor = [100, 340]; #offset = [0, 0];
    #travel = null;
    #ink = '0,0,0'; #accent = '#4658cf'; #groups = [];

    connectedCallback() {
      if (this.#events) return;
      this.#events = new AbortController();
      const options = { signal: this.#events.signal };
      this.#ctx = this.querySelector('canvas')?.getContext('2d');
      this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.#motion.addEventListener('change', () => { this.#updateMotion(); this.#draw(); }, options);
      for (const target of this.querySelectorAll('[data-ag-rg-story], [data-ag-rg-controls]')) {
        target.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.#hover = true; this.#updateMotion(); } }, options);
        target.addEventListener('pointerleave', () => { this.#hover = false; this.#updateMotion(); }, options);
      }
      this.addEventListener('focusin', () => { this.#focused = true; this.#updateMotion(); }, options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) { this.#focused = false; this.#updateMotion(); } }, options);
      document.addEventListener('visibilitychange', () => this.#updateMotion(), { ...options, passive: true });
      this.querySelector('[data-ag-rg-prev]').addEventListener('click', () => this.#show(this.#index - 1, true), options);
      this.querySelector('[data-ag-rg-next]').addEventListener('click', () => this.#show(this.#index + 1, true), options);
      this.querySelector('[data-ag-rg-pause]').addEventListener('click', () => {
        this.#paused = !this.#paused;
        this.#updateMotion();
      }, options);
      this.querySelector('[data-ag-rg-story]').addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const review = this.#reviews[this.#index];
        const widget = document.querySelector('ag-customer-reviews');
        if (review && widget?.openPublicReview?.(review, event.currentTarget)) {
          event.preventDefault(); this.#modalOpen = true; this.#updateMotion();
          widget.querySelector('dialog').addEventListener('close', () => {
            this.#modalOpen = false; this.#updateMotion();
          }, { once: true, signal: this.#events.signal });
        }
      }, options);
      this.#theme = new MutationObserver(() => { this.#colors(); this.#draw(); });
      this.#theme.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
      this.#resize = new ResizeObserver(() => { this.#measure(); this.#draw(); });
      this.#resize.observe(this.querySelector('.ag-rg__stage'));
      // The content column stops growing before the viewport does.
      this.#resize.observe(this.closest('section.ag'));
      const initialCountry = this.querySelector('[data-ag-rg-country]').textContent;
      this.querySelector('[data-ag-rg-flag]').textContent = reviewCountryFlag(countryCode(initialCountry));
      const initialPoint = countries[countryCode(initialCountry)];
      if (initialPoint) {
        this.#point = vector(...initialPoint);
        this.#lat = this.#targetLat = (initialPoint[0] - 12) * radians;
        this.#lon = this.#targetLon = (initialPoint[1] - 16) * radians;
      }
      this.#colors(); this.#measure(); this.#draw();
      this.#visibility = new IntersectionObserver(entries => {
        this.#visible = entries[0].isIntersecting;
        if (this.#visible && !this.#loaded) { this.#loaded = true; void this.#load(this.#events.signal); }
        this.#updateMotion();
      }, { threshold: .08 });
      this.#visibility.observe(this);
    }
    disconnectedCallback() {
      this.#events?.abort(); this.#events = null;
      this.#resize?.disconnect(); this.#visibility?.disconnect(); this.#theme?.disconnect();
      cancelAnimationFrame(this.#frame); this.#frame = null; this.#last = 0;
      this.#travel = null;
      this.#loaded = false; this.#focused = false; this.#hover = false; this.#modalOpen = false;
    }
    #colors() {
      const style = getComputedStyle(this);
      this.#ink = style.getPropertyValue('--ag-ink-rgb').trim().split(/\s+/).join(',') || '0,0,0';
      this.#accent = style.getPropertyValue('--ag-accent').trim() || '#4658cf';
    }
    #measure() {
      const stage = this.querySelector('.ag-rg__stage').getBoundingClientRect();
      const section = this.closest('section.ag').getBoundingClientRect();
      const bleed = Math.max(0, document.documentElement.clientWidth - stage.right);
      const lift = matchMedia('(min-width: 761px)').matches ? section.top - stage.top : 0;
      this.style.setProperty('--ag-rg-bleed', `${bleed}px`);
      this.style.setProperty('--ag-rg-lift', `${lift}px`);
      const canvas = this.querySelector('canvas'), bounds = canvas.getBoundingClientRect();
      this.#width = bounds.width || 560;
      this.#offset = [bounds.left - stage.left, bounds.top - stage.top];
      const size = Math.round(this.#width * Math.min(devicePixelRatio || 1, 2));
      if (canvas.width !== size) { canvas.width = size; canvas.height = size; }
      const card = this.querySelector('[data-ag-rg-story]').getBoundingClientRect();
      this.#anchor = [(card.left - bounds.left + card.width * .75) * 560 / this.#width, (card.top - bounds.top + 5) * 560 / this.#width];
    }
    #permalink(id) {
      if (!/^[a-f0-9-]{36}$/i.test(id)) return '';
      if (isReviewPreview(this)) return previewReviewUrl(id);
      try {
        const base = new URL(this.dataset.form, location.origin);
        const shop = base.pathname.match(/^\/review\/([a-f0-9-]{36})\/?$/i)?.[1];
        return shop && base.protocol === 'https:' && !base.username && !base.password
          ? `${base.origin}/api/reviews/${shop}/permalink/${id}` : '';
      } catch { return ''; }
    }
    async #load(signal) {
      if (!this.dataset.api) return;
      try {
        // Read every public page. A country cluster represents reviews, not street locations.
        const query = new URLSearchParams({ sort: 'newest', pageSize: '100' });
        const seen = new Set(), byCountry = new Map();
        let page = 1, totalPages = 1;
        do {
          query.set('page', String(page));
          const response = await readReviewResponse(this, `${this.dataset.api.replace(/\/$/, '')}/list?${query}`, { signal });
          if (!response.ok) break;
          const data = await response.json();
          if (signal.aborted || !Array.isArray(data.reviews) || !Number.isFinite(data.total)) return;
          totalPages = Math.ceil(data.total / 100);
          for (const review of data.reviews) {
            if (seen.has(review.id) || typeof review.country !== 'string' || typeof review.authorName !== 'string' || typeof review.title !== 'string' || typeof review.body !== 'string' || !Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5 || !this.#permalink(review.id)) continue;
            const code = countryCode(review.country);
            if (!countries[code]) continue;
            seen.add(review.id);
            if (!byCountry.has(code)) byCountry.set(code, []);
            byCountry.get(code).push(review);
          }
          // Interleave countries while retaining every review and its original rating.
          const currentId = this.#reviews[this.#index]?.id;
          const groups = [...byCountry.values()].map(group => group.filter(review => review.rating >= 4));
          this.#reviews = [];
          for (let row = 0; groups.some(group => row < group.length); row++) {
            for (const group of groups) if (group[row]) this.#reviews.push(group[row]);
          }
          this.#groups = [...byCountry].map(([code, reviews]) => ({ point: vector(...countries[code]), count: reviews.length }));
          const count = this.querySelector('[data-ag-rg-map-count]');
          if (count) count.textContent = `${seen.size} reviews · ${byCountry.size} countries`;
          if (this.#reviews.length) {
            this.querySelector('[data-ag-rg-controls]').hidden = this.#reviews.length < 2;
            if (!currentId) this.#show(0);
            else {
              this.#index = Math.max(0, this.#reviews.findIndex(review => review.id === currentId));
              this.querySelector('[data-ag-rg-position]').textContent = `${String(this.#index + 1).padStart(2, '0')} / ${this.#reviews.length}`;
              this.#draw();
            }
            this.#updateMotion();
          }
          if (!data.reviews.length) break;
          page++;
        } while (page <= totalPages && !signal.aborted);
      } catch { /* Keep the server-rendered story and ordinary reviews usable. */ }
    }
    #show(index, manual = false) {
      if (!this.#reviews.length) return;
      this.#index = (index + this.#reviews.length) % this.#reviews.length;
      this.#elapsed = 0;
      const review = this.#reviews[this.#index], coordinates = countries[countryCode(review.country)];
      this.#point = vector(...coordinates);
      this.#targetLat = Math.max(-45, Math.min(55, coordinates[0] - 12)) * radians;
      this.#targetLon = (coordinates[1] - 16) * radians;
      if (this.#motion.matches) {
        this.#travel = null; this.#lat = this.#targetLat; this.#lon = this.#targetLon;
      } else if (manual) {
        // Retarget from the current rendered orientation, including mid-flight clicks.
        this.#travel = { lat: this.#lat, lon: this.#lon, latitude: this.#targetLat - this.#lat,
          longitude: shortAngle(this.#targetLon - this.#lon), elapsed: 0 };
      }
      const set = (name, value) => { this.querySelector(`[data-ag-rg-${name}]`).textContent = value; };
      const card = this.querySelector('[data-ag-rg-story]');
      card.hidden = false; card.href = this.#permalink(review.id);
      set('flag', reviewCountryFlag(countryCode(review.country))); set('country', review.country);
      set('stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
      this.querySelector('[data-ag-rg-rating]').setAttribute('aria-label', `${review.rating} out of 5 stars`);
      set('title', review.title || review.body || 'A customer’s experience'); set('author', review.authorName);
      const date = new Date(review.submittedAt);
      set('date', Number.isFinite(date.getTime()) ? date.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
      this.querySelector('[data-ag-rg-date]').dateTime = Number.isFinite(date.getTime()) ? date.toISOString() : '';
      set('position', `${String(this.#index + 1).padStart(2, '0')} / ${String(this.#reviews.length).padStart(2, '0')}`);
      if (!this.#motion.matches && !manual) {
        this.removeAttribute('data-changing');
        void card.offsetWidth;
        this.setAttribute('data-changing', '');
      }
      this.#measure(); this.#draw(); this.#updateMotion();
    }
    #autoplayStopped() {
      return this.#paused || this.#hover || this.#focused;
    }
    #updateMotion() {
      const suspended = this.#modalOpen || this.#motion.matches || !this.#visible || document.hidden;
      const stopped = suspended || this.#autoplayStopped();
      if (this.#motion.matches && this.#travel) {
        this.#lat = this.#targetLat; this.#lon = this.#targetLon; this.#travel = null;
      }
      this.toggleAttribute('data-paused', stopped);
      const button = this.querySelector('[data-ag-rg-pause]');
      button.setAttribute('aria-pressed', String(this.#paused || this.#motion.matches));
      button.setAttribute('aria-label', this.#motion.matches ? 'Automatic animation disabled by reduced motion preference' : this.#paused ? 'Play globe animation' : 'Pause globe animation');
      button.disabled = this.#motion.matches;
      this.querySelector('[data-ag-rg-pause-icon]').textContent = this.#paused || this.#motion.matches ? '▷' : 'Ⅱ';
      // Hover/focus/pause stop autoplay, but a requested country transition still finishes.
      if (suspended || (stopped && !this.#travel)) { cancelAnimationFrame(this.#frame); this.#frame = null; this.#last = 0; }
      else if (!this.#frame) this.#frame = requestAnimationFrame(time => this.#tick(time));
    }
    #tick(time) {
      this.#frame = requestAnimationFrame(next => this.#tick(next));
      if (this.#last && time - this.#last < 32) return;
      const delta = this.#last ? Math.min(100, time - this.#last) : 0;
      this.#last = time;
      const autoplay = !this.#autoplayStopped() && !this.#travel;
      if (autoplay) this.#elapsed += delta;
      if (autoplay && this.#reviews.length > 1 && this.#elapsed >= Math.max(6, Number(this.dataset.interval) || 8) * 1000) this.#show(this.#index + 1);
      if (this.#travel) {
        const travel = this.#travel;
        travel.elapsed += delta;
        const progress = Math.min(1, travel.elapsed / 1400);
        const easing = progress * progress * (3 - 2 * progress);
        this.#lon = travel.lon + travel.longitude * easing;
        this.#lat = travel.lat + travel.latitude * easing;
        if (progress === 1) { this.#travel = null; this.#updateMotion(); }
      } else if (autoplay) {
        const easing = 1 - Math.exp(-delta / 850);
        this.#lon += shortAngle(this.#targetLon - this.#lon) * easing;
        this.#lat += (this.#targetLat - this.#lat) * easing;
        this.#targetLon += delta * .000002;
      }
      this.#draw();
    }
    #draw() {
      const ctx = this.#ctx;
      if (!ctx) return;
      const scale = ctx.canvas.width / 560;
      ctx.setTransform(scale, 0, 0, scale, 0, 0); ctx.clearRect(0, 0, 560, 560);
      const cx = 350, cy = 228, radius = 322;
      const glow = ctx.createRadialGradient(cx - 80, cy - 100, 20, cx, cy, radius);
      glow.addColorStop(0, `rgba(${this.#ink},0)`); glow.addColorStop(.7, `rgba(${this.#ink},.012)`); glow.addColorStop(1, `rgba(${this.#ink},.045)`);
      ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2); ctx.fill();
      const cosLon = Math.cos(this.#lon), sinLon = Math.sin(this.#lon), cosLat = Math.cos(this.#lat), sinLat = Math.sin(this.#lat);
      const project = ([x, y, z]) => { const a = x * cosLon - z * sinLon, b = x * sinLon + z * cosLon; return [cx + radius * a, cy - radius * (y * cosLat - b * sinLat), y * sinLat + b * cosLat]; };
      const bins = Array.from({ length: 8 }, () => []);
      for (const point of points) { const [x, y, z] = project(point); if (z > 0) bins[Math.min(7, Math.floor(z * 8))].push(x, y); }
      bins.forEach((bin, depth) => {
        ctx.beginPath(); const size = .8 + depth * .065;
        for (let i = 0; i < bin.length; i += 2) { ctx.moveTo(bin[i] + size, bin[i + 1]); ctx.arc(bin[i], bin[i + 1], size, 0, Math.PI * 2); }
        ctx.fillStyle = `rgba(${this.#ink},${.1 + depth * .065})`; ctx.fill();
      });
      // One dot per mapped review. Pack dots around the country centroid; the
      // offsets are a count visualization, never invented customer coordinates.
      ctx.fillStyle = this.#accent;
      for (const group of this.#groups) {
        const [x, y, z] = project(group.point);
        if (z <= .08) continue;
        ctx.globalAlpha = .4 + z * .4;
        ctx.beginPath();
        for (let i = 0; i < group.count; i++) {
          const angle = i * 2.399963229728653, spread = 3.3 * Math.sqrt(i);
          const px = x + Math.cos(angle) * spread, py = y + Math.sin(angle) * spread;
          ctx.moveTo(px + 1.45, py); ctx.arc(px, py, 1.45, 0, Math.PI * 2);
        }
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      const marker = this.querySelector('[data-ag-rg-marker]'), line = this.querySelector('[data-ag-rg-line]');
      if (!this.#point) return;
      const [x, y, z] = project(this.#point);
      marker.hidden = z <= .08;
      marker.style.transform = `translate(${x * this.#width / 560 + this.#offset[0] - 6}px,${y * this.#width / 560 + this.#offset[1] - 6}px)`;
      line.setAttribute('d', z > .08 ? `M ${x} ${y} Q ${x + 25} ${(y + this.#anchor[1]) / 2} ${this.#anchor[0]} ${this.#anchor[1]}` : '');
    }
  });
}
